import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { decodeHtml, encodeHtml, srcDoc } from "@/lib/kbHtmlSandbox";

/** Paste an interactive snippet (markup + inline style + inline script) and
 *  see it run in the same sandbox the reader will get. */
export function KbHtmlBlockDialog({ open, onOpenChange, initial, onSave }) {
  const [html, setHtml] = useState("");
  const [title, setTitle] = useState("");
  const [height, setHeight] = useState(320);
  const [preview, setPreview] = useState("");
  const frameRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const source = initial?.html ? decodeHtml(initial.html) : "";
    setHtml(source);
    setTitle(initial?.title || "");
    setHeight(Number(initial?.height) || 320);
    setPreview(source ? srcDoc(source) : "");
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const run = () => setPreview(srcDoc(html));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="kb-html-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit interactive block" : "Interactive block"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">HTML, CSS and JavaScript</Label>
            <textarea data-testid="kb-html-source" value={html} rows={16} spellCheck={false}
              onChange={(e) => setHtml(e.target.value)}
              placeholder={"<style>…</style>\n<div>…</div>\n<script>…</script>"}
              className="w-full rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-2.5 py-2 font-mono text-[11px] leading-relaxed" />
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Label (for editors and screen readers)</Label>
                <Input data-testid="kb-html-title" value={title} className="h-8 text-xs"
                  onChange={(e) => setTitle(e.target.value)} placeholder="Voice sample picker" />
              </div>
              <div className="w-24 space-y-1">
                <Label className="text-xs">Min height</Label>
                <Input data-testid="kb-html-height" type="number" value={height} className="h-8 text-xs"
                  onChange={(e) => setHeight(Number(e.target.value) || 320)} />
              </div>
              <Button variant="outline" size="sm" className="h-8" data-testid="kb-html-run"
                onClick={run}>Run it</Button>
            </div>
            <p className="flex items-start gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              Runs in a sealed frame: it can show images, audio and video over https,
              but it can&apos;t read the page around it, your sign-in, or send data anywhere.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Preview — exactly what a reader gets</Label>
            <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-2">
              {preview ? (
                <iframe ref={frameRef} title="Interactive block preview" srcDoc={preview}
                  sandbox="allow-scripts" referrerPolicy="no-referrer"
                  data-testid="kb-html-preview"
                  style={{ width: "100%", height: `${Math.max(160, height)}px`, border: 0 }} />
              ) : (
                <p className="py-16 text-center text-xs text-slate-400">
                  Paste your snippet, then press “Run it”.
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="kb-html-save" disabled={!html.trim()}
            onClick={() => {
              onSave({ html: encodeHtml(html), height: Math.max(80, Number(height) || 320), title });
              onOpenChange(false);
            }}>
            {initial ? "Update block" : "Insert block"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
